import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
} from '@mui/material';
import type { LoginChallengeResponse, LoginResponse, SessionUser } from '@pd/shared';
import { api, ApiError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { t } from '../../i18n/sq';
import { PasswordField } from '../../components/PasswordField';
import { AuthShell } from './AuthShell';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<LoginChallengeResponse | null>(null);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from ?? '/';

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.common.unexpectedError);
    } finally {
      setBusy(false);
    }
  }

  const submitCredentials = (e?: FormEvent) => {
    e?.preventDefault();
    return run(async () => {
      const res = await api.post<LoginResponse>('/auth/login', { email, password });
      if (res.status === 'authenticated') {
        // This browser is a trusted device: no code needed.
        setUser(res.user);
        navigate(from, { replace: true });
        return;
      }
      setChallenge(res);
      setCode('');
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    return run(async () => {
      const user = await api.post<SessionUser>('/auth/verify', {
        challengeId: challenge!.challengeId,
        code,
        rememberDevice,
      });
      setUser(user);
      navigate(from, { replace: true });
    });
  };

  if (challenge) {
    return (
      <AuthShell title={t.auth.otpTitle} subtitle={t.auth.otpSubtitle(challenge.sentTo)}>
        <Box component="form" onSubmit={submitCode}>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="info" variant="outlined">
              {t.auth.otpSpamHint}
            </Alert>
            <TextField
              label={t.auth.otpCode}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              autoComplete="one-time-code"
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  style: { letterSpacing: 8, fontSize: 22, textAlign: 'center' },
                },
              }}
            />
            {challenge.trustDeviceDays > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                  />
                }
                label={t.auth.rememberDevice(challenge.trustDeviceDays)}
                slotProps={{ typography: { variant: 'body2' } }}
              />
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              loading={busy}
              disabled={code.length !== 6}
            >
              {t.auth.verify}
            </Button>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Link component="button" type="button" onClick={() => setChallenge(null)}>
                {t.auth.useAnotherAccount}
              </Link>
              <Link component="button" type="button" onClick={() => void submitCredentials()}>
                {t.auth.resendCode}
              </Link>
            </Stack>
          </Stack>
        </Box>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t.auth.signInTitle} subtitle={t.auth.signInSubtitle}>
      <Box component="form" onSubmit={submitCredentials}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t.auth.email}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
          <PasswordField
            label={t.auth.password}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <Button type="submit" variant="contained" size="large" loading={busy}>
            {t.auth.signIn}
          </Button>
          <Link component={RouterLink} to="/harruat-fjalekalimin" sx={{ textAlign: 'center' }}>
            {t.auth.forgotPassword}
          </Link>
        </Stack>
      </Box>
    </AuthShell>
  );
}
